import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventServiceResponse, EventServiceRegistrationResponse, CompanyResponse} from '../../../../../../../../../../@shared/models/response.module';
import {formatDateDMYDots} from '../../../../../../../../../../@core/utils/utils.service';
import {DeleteModal} from '../../../../../../../../../../@shared/components/delete-modal/delete-modal';
import {EventsService} from '../../../../../../../../../../@shared/services/events-service';
import {ToastService} from '../../../../../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {EditServiceRegistration} from './components/edit-service-registration/edit-service-registration';
import {AddServiceRegistration} from './components/add-service-registration/add-service-registration';
import {AQH_EVE_11, AQH_EVE_33, AQH_EVE_151} from '../../../../../../../../../../@shared/models/input.module';
import {EndpointService} from '../../../../../../../../../../@shared/endpoints.service';
import {StorageService} from '../../../../../../../../../../@core/services/storage.service';

@Component({
  selector: 'app-view-service-registrations',
  standalone: false,
  templateUrl: './view-service-registrations.html',
  styleUrl: './view-service-registrations.css'
})
export class ViewServiceRegistrations implements OnInit {
  @Input() eventService!: EventServiceResponse;
  @Input() companyId!: string;
  @Input() company?: CompanyResponse;
  loading = false;
  hasUpdates = false;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private eventsService: EventsService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private storageService: StorageService,
    private endpointService: EndpointService
  ) {}

  ngOnInit() {
    console.log('Event Service with Registrations:', this.eventService);
  }

  get customPaymentMethodName(): string | null {
    return this.company?.custom_payment_method_name || null;
  }

  get hasCustomPayment(): boolean {
    return this.company?.custom_payment_enabled === true;
  }

  reloadEventService() {
    if (!this.eventService) return;

    this.loading = true;
    const payload: AQH_EVE_11 = {
      event_id: this.eventService.event_id
    };

    this.eventsService.getEventServices(payload).subscribe({
      next: (services: EventServiceResponse[]) => {
        // Find and update the current event service
        const updatedService = services.find(s => s.event_service_id === this.eventService.event_service_id);
        if (updatedService) {
          this.eventService = updatedService;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error(this.translateService.instant('manageEvent.viewServiceRegistrations.reloadError'));
      }
    });
  }

  close() {
    this.activeModal.close(this.hasUpdates);
  }

  getRegistrationNumber(index: number): number {
    return index + 1;
  }

  getTotalRegistrations(): number {
    if (!this.eventService.event_service_registrations || this.eventService.event_service_registrations.length === 0) {
      return 0;
    }
    return this.eventService.event_service_registrations.reduce((total, registration) => {
      return total + (registration.num_registrations || 0);
    }, 0);
  }

  openAddRegistrationModal() {
    const modalRef = this.modalService.open(AddServiceRegistration, { size: 'lg', centered: true });
    modalRef.componentInstance.eventService = this.eventService;

    modalRef.result.then((added) => {
      if (added) {
        // Mark that updates were made
        this.hasUpdates = true;
        // Reload the event service data to show new registration
        this.reloadEventService();
      }
    }, () => {
      // Modal dismissed
    });
  }

  openEditRegistrationModal(registration: EventServiceRegistrationResponse) {
    const modalRef = this.modalService.open(EditServiceRegistration, { size: 'lg', centered: true });
    modalRef.componentInstance.registration = registration;

    modalRef.result.then((updated) => {
      if (updated) {
        // Mark that updates were made
        this.hasUpdates = true;
        // Reload the event service data to show updated registration
        this.reloadEventService();
      }
    }, () => {
      // Modal dismissed
    });
  }

  deleteRegistration(registration: EventServiceRegistrationResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.result.then((confirmed) => {
      if (confirmed) {
        const payload: AQH_EVE_33 = {
          event_service_registration_id: registration.event_service_registration_id,
          user_id: this.storageService.getUser().user_id
        };

        this.eventsService.cancelEventServiceRegistration(payload).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.viewServiceRegistrations.cancelSuccess'));
            // Mark that updates were made
            this.hasUpdates = true;
            // Remove the registration from the list
            const index = this.eventService.event_service_registrations.findIndex(
              r => r.event_service_registration_id === registration.event_service_registration_id
            );
            if (index > -1) {
              this.eventService.event_service_registrations.splice(index, 1);
            }
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.viewServiceRegistrations.cancelError'));
          }
        });
      }
    }, () => {
      // Modal dismissed
    });
  }

  markAsPaid(registration: EventServiceRegistrationResponse, paymentMethod: 'cash' | 'bank_transfer' | 'other') {
    const payload: AQH_EVE_151 = {
      company_id: this.companyId,
      event_service_registration_id: registration.event_service_registration_id,
      payment_status: 'paid',
      payment_method: paymentMethod
    };

    this.endpointService.aqh_eve_151(payload).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('manageEvent.viewServiceRegistrations.markPaidSuccess'));
        // Update the registration in the list
        registration.payment_status = 'paid';
        registration.payment_method = paymentMethod;
        this.hasUpdates = true;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('manageEvent.viewServiceRegistrations.markPaidError'));
      }
    });
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00 per transaction
   */
  calculateNetAmount(grossAmount: number, transactionCount: number): number {
    if (!grossAmount || grossAmount <= 0) return 0;
    const net = grossAmount * 0.956 - (2 * transactionCount);
    return Math.max(0, Math.round(net * 100) / 100);
  }

  getTotalRevenue(): number {
    const totalRegs = this.getTotalRegistrations();
    const price = this.eventService?.price || 0;
    return totalRegs * price;
  }

  getNetRevenue(): number {
    const grossRevenue = this.getTotalRevenue();
    const transactionCount = this.eventService?.event_service_registrations?.length || 0;
    return this.calculateNetAmount(grossRevenue, transactionCount);
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
